import type {
    ActionContext,
    FormDataMap,
    FormElementConfig,
    FormErrors,
    FormFieldConfig,
    FormFieldValue,
    FormStatus,
    FormValues,
    SubmitCallbacks,
    SubmitResult,
} from '../types/form-builder.types';
import { DEFAULT_FIELD_STATE, type FieldState, type FormStoreConfig } from '../types/form-store.types';
import { resolveDynamicStringValue } from '../utils/dynamic-value.utils';
import { resolveCondition } from '../utils/resolve-condition.utils';
import { getDefaultValue, isFieldConfig, isValueEqual, normalizeFieldValue } from './utils/field.utils';
import { buildElementMaps, buildReverseDependencies, initializeElementStates } from './utils/initialization.utils';
import {
    createValidationRunner,
    getMaxDebounceTime,
    hasCustomValidators,
    isFieldRequired,
} from './utils/validation.utils';

export const createFormStore = (config: FormStoreConfig) => {
    let currentData = config.data;
    const {
        runCustomValidationOnly,
        runSynchronousValidation,
        runFullValidation,
        shouldRunCustomValidation,
        startCustomValidationOnly,
    } = createValidationRunner(() => currentData);

    // --- Submit-level state ---

    let submitValidating = false;
    let isSubmitting = false;
    let isSubmitSuccessful = false;
    let submitError: unknown;

    // --- Setup ---

    const fields = config.elements.filter(isFieldConfig);
    const { elementConfigs, fieldConfigs } = buildElementMaps(config.elements);

    const reverseDependencies = buildReverseDependencies(config.elements);
    const { fieldStates, initialValues } = initializeElementStates(config.elements, config.defaultValues, config.data);

    // --- Values snapshot ---

    let valuesSnapshot: FormValues = { ...initialValues };

    const hasSnapshotValue = (fieldId: string): boolean =>
        Object.prototype.hasOwnProperty.call(valuesSnapshot, fieldId);

    const setSnapshotValue = (fieldId: string, value: FormFieldValue): boolean => {
        if (hasSnapshotValue(fieldId) && isValueEqual(valuesSnapshot[fieldId] ?? '', value)) {
            return false;
        }

        valuesSnapshot = { ...valuesSnapshot, [fieldId]: value };
        return true;
    };

    const removeSnapshotValue = (fieldId: string): boolean => {
        if (!hasSnapshotValue(fieldId)) {
            return false;
        }

        const { [fieldId]: _, ...remaining } = valuesSnapshot;
        valuesSnapshot = remaining;
        return true;
    };

    // Post-initialization: remove values for fields that are not initially rendered.
    for (const field of fields) {
        if (!fieldStates.get(field.id)?.rendered) {
            removeSnapshotValue(field.id);
        }
    }

    // --- Subscriptions ---

    const fieldListeners = new Map<string, Set<() => void>>();
    const formListeners = new Set<() => void>();

    const notifyFormListeners = () => {
        for (const listener of formListeners) {
            listener();
        }
    };

    const notifyFieldListeners = (fieldId: string) => {
        const listeners = fieldListeners.get(fieldId);
        if (listeners) {
            for (const listener of listeners) {
                listener();
            }
        }
        notifyFormListeners();
    };

    // --- Field state updates ---

    const updateFieldState = (fieldId: string, updates: Partial<FieldState>): boolean => {
        const current = fieldStates.get(fieldId);
        if (!current) {
            return false;
        }

        const next: FieldState = { ...current, ...updates };

        if (
            next.value === current.value &&
            next.error === current.error &&
            next.isTouched === current.isTouched &&
            next.disabled === current.disabled &&
            next.hidden === current.hidden &&
            next.helperText === current.helperText &&
            next.required === current.required &&
            next.rendered === current.rendered &&
            next.validating === current.validating
        ) {
            return false;
        }

        fieldStates.set(fieldId, next);
        return true;
    };

    // --- Async validation scheduling ---

    const asyncTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const asyncVersions = new Map<string, number>();

    const cancelPendingValidation = (fieldId: string) => {
        const timer = asyncTimers.get(fieldId);
        if (timer) {
            clearTimeout(timer);
            asyncTimers.delete(fieldId);
        }
    };

    const startValidationRun = (fieldId: string): number => {
        cancelPendingValidation(fieldId);

        const version = (asyncVersions.get(fieldId) ?? 0) + 1;
        asyncVersions.set(fieldId, version);

        return version;
    };

    const invalidateValidationRun = (fieldId: string): void => {
        startValidationRun(fieldId);
    };

    const executePendingValidation = async (
        fieldId: string,
        fieldConfig: FormFieldConfig,
        version: number
    ): Promise<void> => {
        const fieldState = fieldStates.get(fieldId);
        if (!fieldState?.rendered) {
            const changed = updateFieldState(fieldId, { validating: false });
            if (changed) {
                notifyFieldListeners(fieldId);
            }
            return;
        }

        try {
            const asyncError = await runCustomValidationOnly(fieldState.value, valuesSnapshot, fieldConfig);

            if (asyncVersions.get(fieldId) !== version) {
                return;
            }

            const changed = updateFieldState(fieldId, { error: asyncError, validating: false });
            if (changed) {
                notifyFieldListeners(fieldId);
            }
        } catch {
            if (asyncVersions.get(fieldId) === version) {
                const changed = updateFieldState(fieldId, { validating: false });
                if (changed) {
                    notifyFieldListeners(fieldId);
                }
            }
        }
    };

    const scheduleValidation = (fieldId: string, fieldConfig: FormFieldConfig) => {
        const version = startValidationRun(fieldId);

        const debounceMs = getMaxDebounceTime(fieldConfig);

        const timer = setTimeout(() => {
            asyncTimers.delete(fieldId);
            void executePendingValidation(fieldId, fieldConfig, version);
        }, debounceMs);

        asyncTimers.set(fieldId, timer);
    };

    const runCustomValidationNow = ({
        fieldConfig,
        fieldId,
        updates,
        value,
    }: {
        readonly fieldConfig: FormFieldConfig;
        readonly fieldId: string;
        readonly updates: Partial<FieldState>;
        readonly value: FormFieldValue;
    }) => {
        const version = startValidationRun(fieldId);

        try {
            const customValidation = startCustomValidationOnly(value, valuesSnapshot, fieldConfig);
            if (customValidation.type === 'sync') {
                const changed = updateFieldState(fieldId, {
                    ...updates,
                    error: customValidation.error,
                    validating: false,
                });
                if (changed) {
                    notifyFieldListeners(fieldId);
                }
                return;
            }

            const changed = updateFieldState(fieldId, {
                ...updates,
                error: undefined,
                validating: true,
            });
            if (changed) {
                notifyFieldListeners(fieldId);
            }

            void customValidation.validation
                .then((validationError) => {
                    if (asyncVersions.get(fieldId) !== version) {
                        return;
                    }
                    const changed = updateFieldState(fieldId, {
                        error: validationError,
                        validating: false,
                    });
                    if (changed) {
                        notifyFieldListeners(fieldId);
                    }
                })
                .catch(() => {
                    if (asyncVersions.get(fieldId) === version) {
                        const changed = updateFieldState(fieldId, { validating: false });
                        if (changed) {
                            notifyFieldListeners(fieldId);
                        }
                    }
                });
        } catch {
            const changed = updateFieldState(fieldId, {
                ...updates,
                error: undefined,
                validating: false,
            });
            if (changed) {
                notifyFieldListeners(fieldId);
            }
        }
    };

    // --- Element state refresh ---

    const refreshElementState = (elementId: string): boolean => {
        const elementConfig = elementConfigs.get(elementId);
        const state = fieldStates.get(elementId);
        if (!elementConfig || !state) {
            return false;
        }

        const nextRendered =
            elementConfig.showWhen === undefined
                ? true
                : resolveCondition(elementConfig.showWhen, valuesSnapshot, currentData);
        const fieldConfig = fieldConfigs.get(elementId);

        const effectiveValueChanged = fieldConfig
            ? nextRendered
                ? setSnapshotValue(elementId, state.value)
                : removeSnapshotValue(elementId)
            : false;

        if (!nextRendered) {
            invalidateValidationRun(elementId);
            const changed = updateFieldState(elementId, {
                error: undefined,
                validating: false,
                disabled: false,
                hidden: false,
                helperText: undefined,
                required: false,
                rendered: false,
            });
            if (changed) {
                notifyFieldListeners(elementId);
            }
            return effectiveValueChanged;
        }

        const hidden =
            elementConfig.hidden !== undefined
                ? resolveCondition(elementConfig.hidden, valuesSnapshot, currentData)
                : false;

        if (!fieldConfig) {
            const changed = updateFieldState(elementId, {
                disabled:
                    elementConfig.disabled !== undefined
                        ? resolveCondition(elementConfig.disabled, valuesSnapshot, currentData)
                        : false,
                hidden,
                rendered: true,
            });
            if (changed) {
                notifyFieldListeners(elementId);
            }
            return false;
        }

        const shouldValidateOnRefresh = state.isTouched || state.error !== undefined;
        const syncError = shouldValidateOnRefresh
            ? runSynchronousValidation(state.value, valuesSnapshot, fieldConfig)
            : undefined;

        const shouldRunCustom =
            shouldValidateOnRefresh &&
            !syncError &&
            shouldRunCustomValidation(state.value, valuesSnapshot, fieldConfig);
        const shouldDebounceCustomValidation = shouldRunCustom && getMaxDebounceTime(fieldConfig) > 0;
        const fieldUpdates = {
            disabled:
                fieldConfig.disabled !== undefined
                    ? resolveCondition(fieldConfig.disabled, valuesSnapshot, currentData)
                    : false,
            hidden,
            helperText: resolveDynamicStringValue({
                value: fieldConfig.helperText,
                formValues: valuesSnapshot,
                data: currentData,
            }),
            isTouched: state.isTouched,
            rendered: true,
            required: isFieldRequired(fieldConfig, valuesSnapshot, currentData),
        };

        if (!shouldRunCustom) {
            invalidateValidationRun(elementId);
            const changed = updateFieldState(elementId, {
                ...fieldUpdates,
                error: syncError,
                validating: false,
            });
            if (changed) {
                notifyFieldListeners(elementId);
            }
        } else if (shouldDebounceCustomValidation) {
            const changed = updateFieldState(elementId, {
                ...fieldUpdates,
                error: syncError,
                validating: true,
            });
            if (changed) {
                notifyFieldListeners(elementId);
            }
            scheduleValidation(elementId, fieldConfig);
        } else {
            runCustomValidationNow({
                fieldConfig,
                fieldId: elementId,
                updates: fieldUpdates,
                value: state.value,
            });
        }

        return effectiveValueChanged;
    };

    // --- Dependency propagation ---

    const refreshDependentElements = (changedFieldIds: readonly string[]) => {
        const fieldsToProcess = [...changedFieldIds];
        const alreadyQueued = new Set(fieldsToProcess);
        const timesVisited = new Map<string, number>();
        const maxVisitsPerElement = Math.max(1, elementConfigs.size);

        while (fieldsToProcess.length > 0) {
            const currentFieldId = fieldsToProcess.shift();
            if (!currentFieldId) {
                continue;
            }

            const visitCount = (timesVisited.get(currentFieldId) ?? 0) + 1;
            if (visitCount > maxVisitsPerElement) {
                return;
            }

            timesVisited.set(currentFieldId, visitCount);
            alreadyQueued.delete(currentFieldId);
            const dependents = reverseDependencies.get(currentFieldId);
            if (!dependents) {
                continue;
            }

            for (const dependentId of dependents) {
                const effectiveValueChanged = refreshElementState(dependentId);

                if (effectiveValueChanged && !alreadyQueued.has(dependentId)) {
                    fieldsToProcess.push(dependentId);
                    alreadyQueued.add(dependentId);
                }
            }
        }
    };

    // --- Field operations ---

    const setFieldValue = (fieldId: string, value: FormFieldValue) => {
        const fieldConfig = fieldConfigs.get(fieldId);
        const state = fieldStates.get(fieldId);
        if (!fieldConfig || !state) {
            return;
        }

        const normalizedValue = normalizeFieldValue(fieldConfig, value);

        // Clear submit success on any field edit
        if (isSubmitSuccessful) {
            isSubmitSuccessful = false;
        }

        if (!state.rendered) {
            invalidateValidationRun(fieldId);
            const changed = updateFieldState(fieldId, { value: normalizedValue });
            if (changed) {
                notifyFieldListeners(fieldId);
            }
            return;
        }

        setSnapshotValue(fieldId, normalizedValue);

        const changeEnabled = fieldConfig.validateOn?.change !== false;
        if (changeEnabled) {
            const syncError = runSynchronousValidation(normalizedValue, valuesSnapshot, fieldConfig);
            const shouldValidateCustom =
                !syncError && shouldRunCustomValidation(normalizedValue, valuesSnapshot, fieldConfig);
            const shouldDebounceCustomValidation = shouldValidateCustom && getMaxDebounceTime(fieldConfig) > 0;

            if (!shouldValidateCustom) {
                invalidateValidationRun(fieldId);
                const changed = updateFieldState(fieldId, {
                    value: normalizedValue,
                    error: syncError,
                    validating: false,
                });
                if (changed) {
                    notifyFieldListeners(fieldId);
                }
            } else if (shouldDebounceCustomValidation) {
                const changed = updateFieldState(fieldId, {
                    value: normalizedValue,
                    error: syncError,
                    validating: true,
                });
                if (changed) {
                    notifyFieldListeners(fieldId);
                }
                scheduleValidation(fieldId, fieldConfig);
            } else {
                runCustomValidationNow({
                    fieldConfig,
                    fieldId,
                    updates: {
                        value: normalizedValue,
                    },
                    value: normalizedValue,
                });
            }
        } else {
            invalidateValidationRun(fieldId);
            const changed = updateFieldState(fieldId, { value: normalizedValue });
            if (changed) {
                notifyFieldListeners(fieldId);
            }
        }

        refreshDependentElements([fieldId]);
    };

    const handleBlur = (fieldId: string) => {
        const fieldConfig = fieldConfigs.get(fieldId);
        if (!fieldConfig) {
            return;
        }

        const blurEnabled = fieldConfig.validateOn?.blur !== false;
        const state = fieldStates.get(fieldId);

        if (!state?.rendered) {
            return;
        }

        if (!blurEnabled || !fieldConfig.validators?.length) {
            const changed = updateFieldState(fieldId, { isTouched: true });
            if (changed) {
                notifyFieldListeners(fieldId);
            }
            return;
        }

        const syncError = runSynchronousValidation(state.value, valuesSnapshot, fieldConfig);
        const shouldValidateCustom = !syncError && shouldRunCustomValidation(state.value, valuesSnapshot, fieldConfig);

        if (!shouldValidateCustom) {
            invalidateValidationRun(fieldId);
            const changed = updateFieldState(fieldId, {
                isTouched: true,
                error: syncError,
                validating: false,
            });
            if (changed) {
                notifyFieldListeners(fieldId);
            }
            return;
        }

        runCustomValidationNow({
            fieldConfig,
            fieldId,
            updates: { isTouched: true },
            value: state.value,
        });
    };

    const validateField = async (fieldId: string): Promise<string | undefined> => {
        const fieldConfig = fieldConfigs.get(fieldId);
        const state = fieldStates.get(fieldId);
        if (!fieldConfig || !state?.rendered) {
            return undefined;
        }

        const version = startValidationRun(fieldId);
        const error = await runFullValidation(state.value, valuesSnapshot, fieldConfig);

        if (asyncVersions.get(fieldId) !== version) {
            return undefined;
        }

        const changed = updateFieldState(fieldId, { isTouched: true, error, validating: false });
        if (changed) {
            notifyFieldListeners(fieldId);
        }
        return error;
    };

    const executeAction = (action: (context: ActionContext) => void | Promise<void>): void | Promise<void> => {
        return action({
            validateField,
            getValues: () => ({ ...valuesSnapshot }),
        });
    };

    // --- Form submission ---

    const getRenderedValues = (): FormValues => {
        const values: FormValues = {};
        for (const field of fields) {
            const state = fieldStates.get(field.id);
            if (state?.rendered) {
                values[field.id] = state.value;
            }
        }
        return values;
    };

    const submit = async (callbacks?: SubmitCallbacks): Promise<SubmitResult> => {
        if (submitValidating || isSubmitting) {
            return { values: {}, errors: {}, ok: false };
        }

        // 1. Capture frozen snapshot
        const snapshot = getRenderedValues();

        // 2. Validation phase
        submitValidating = true;
        notifyFormListeners();

        for (const fieldId of Array.from(asyncTimers.keys())) {
            invalidateValidationRun(fieldId);
        }

        const formErrors: FormErrors = {};
        const asyncValidations: Array<Promise<void>> = [];

        for (const field of fields) {
            const state = fieldStates.get(field.id);
            if (!state?.rendered) {
                continue;
            }

            const fieldValue = snapshot[field.id];
            if (fieldValue === undefined) {
                continue;
            }

            if (hasCustomValidators(field)) {
                const version = startValidationRun(field.id);

                asyncValidations.push(
                    runFullValidation(fieldValue, snapshot, field).then((validationError) => {
                        if (validationError !== undefined) {
                            formErrors[field.id] = validationError;
                        }

                        if (asyncVersions.get(field.id) !== version) {
                            return;
                        }

                        const changed = updateFieldState(field.id, {
                            isTouched: true,
                            error: validationError,
                            validating: false,
                        });
                        if (changed) {
                            notifyFieldListeners(field.id);
                        }
                    })
                );
            } else {
                const validationError = runSynchronousValidation(fieldValue, snapshot, field);
                const changed = updateFieldState(field.id, {
                    isTouched: true,
                    error: validationError,
                    validating: false,
                });
                if (changed) {
                    notifyFieldListeners(field.id);
                }
                if (validationError !== undefined) {
                    formErrors[field.id] = validationError;
                }
            }
        }

        if (asyncValidations.length > 0) {
            try {
                await Promise.all(asyncValidations);
            } catch (error: unknown) {
                submitValidating = false;
                isSubmitSuccessful = false;
                submitError = error;
                notifyFormListeners();
                callbacks?.onSubmitFailure?.(error);
                return { values: snapshot, errors: formErrors, ok: false };
            }
        }

        submitValidating = false;

        // 3. Check validation results
        if (Object.keys(formErrors).length > 0) {
            notifyFormListeners();
            callbacks?.onSubmitError?.(formErrors);
            return { values: snapshot, errors: formErrors, ok: false };
        }

        // 4. Submit phase (async onSubmit)
        if (callbacks?.onSubmit) {
            isSubmitting = true;
            notifyFormListeners();

            try {
                await callbacks.onSubmit(snapshot);
                isSubmitting = false;
                isSubmitSuccessful = true;
                submitError = undefined;
                notifyFormListeners();
            } catch (error: unknown) {
                isSubmitting = false;
                isSubmitSuccessful = false;
                submitError = error;
                notifyFormListeners();
                callbacks.onSubmitFailure?.(error);
                return { values: snapshot, errors: {}, ok: false };
            }
        } else {
            isSubmitSuccessful = true;
            submitError = undefined;
            notifyFormListeners();
        }

        return { values: snapshot, errors: {}, ok: true };
    };

    // --- Reset ---

    const reset = () => {
        // Cancel all pending validations
        for (const timer of asyncTimers.values()) {
            clearTimeout(timer);
        }
        asyncTimers.clear();
        asyncVersions.clear();

        // Reset submit state
        submitValidating = false;
        isSubmitting = false;
        isSubmitSuccessful = false;
        submitError = undefined;

        // Rebuild values snapshot with all initial values
        valuesSnapshot = { ...initialValues };

        // Re-initialize element states with current data
        for (const element of config.elements) {
            const fieldConfig = fieldConfigs.get(element.id);
            const rendered =
                element.showWhen === undefined ? true : resolveCondition(element.showWhen, valuesSnapshot, currentData);
            const hidden =
                rendered && element.hidden !== undefined
                    ? resolveCondition(element.hidden, valuesSnapshot, currentData)
                    : false;

            fieldStates.set(element.id, {
                value: fieldConfig ? (initialValues[element.id] ?? getDefaultValue(fieldConfig)) : '',
                error: undefined,
                isTouched: false,
                disabled:
                    rendered && element.disabled !== undefined
                        ? resolveCondition(element.disabled, valuesSnapshot, currentData)
                        : false,
                hidden,
                helperText:
                    rendered && fieldConfig !== undefined
                        ? resolveDynamicStringValue({
                              value: fieldConfig.helperText,
                              formValues: valuesSnapshot,
                              data: currentData,
                          })
                        : undefined,
                required:
                    rendered && fieldConfig !== undefined
                        ? isFieldRequired(fieldConfig, valuesSnapshot, currentData)
                        : false,
                rendered,
                validating: false,
            });
        }

        // Prune non-rendered field values from snapshot
        for (const field of fields) {
            if (!fieldStates.get(field.id)?.rendered) {
                removeSnapshotValue(field.id);
            }
        }

        // Propagate dependencies
        refreshDependentElements(fields.map((f) => f.id));

        // Notify all listeners
        for (const element of config.elements) {
            notifyFieldListeners(element.id);
        }
        notifyFormListeners();
    };

    // --- Data update ---

    const updateData = (newData?: FormDataMap) => {
        currentData = newData;

        // Refresh all elements to re-evaluate data-driven conditions
        for (const element of config.elements) {
            refreshElementState(element.id);
        }

        // Propagate any visibility changes through the dependency graph
        refreshDependentElements(fields.map((field) => field.id));
    };

    // --- Initial dependency propagation ---
    refreshDependentElements(fields.map((field) => field.id));

    // --- Form-level state snapshot ---

    let cachedFormSnapshot: FormStatus | undefined;

    const getFormSnapshot = (): FormStatus => {
        const isValidValue = fields.every((field) => {
            const state = fieldStates.get(field.id);
            return !state?.rendered || state.error === undefined;
        });

        const isDirtyValue = fields.some((field) => {
            const state = fieldStates.get(field.id);
            if (!state?.rendered) {
                return false;
            }
            return !isValueEqual(state.value, initialValues[field.id] ?? getDefaultValue(field));
        });

        const isValidatingValue =
            submitValidating ||
            fields.some((field) => {
                const state = fieldStates.get(field.id);
                return state?.rendered && state.validating;
            });

        if (
            cachedFormSnapshot?.isSubmitting === isSubmitting &&
            cachedFormSnapshot.isValid === isValidValue &&
            cachedFormSnapshot.isDirty === isDirtyValue &&
            cachedFormSnapshot.isValidating === isValidatingValue &&
            cachedFormSnapshot.isSubmitSuccessful === isSubmitSuccessful &&
            cachedFormSnapshot.submitError === submitError
        ) {
            return cachedFormSnapshot;
        }

        cachedFormSnapshot = {
            isSubmitting,
            isValid: isValidValue,
            isDirty: isDirtyValue,
            isValidating: isValidatingValue,
            isSubmitSuccessful,
            submitError,
        };
        return cachedFormSnapshot;
    };

    // --- Public API ---

    return {
        config: { id: config.id, elements: config.elements },

        subscribeField(fieldId: string, callback: () => void): () => void {
            const existing = fieldListeners.get(fieldId);
            if (existing) {
                existing.add(callback);
            } else {
                fieldListeners.set(fieldId, new Set([callback]));
            }
            return () => {
                fieldListeners.get(fieldId)?.delete(callback);
            };
        },

        getFieldSnapshot(fieldId: string): FieldState {
            return fieldStates.get(fieldId) ?? DEFAULT_FIELD_STATE;
        },

        subscribeForm(callback: () => void): () => void {
            formListeners.add(callback);
            return () => {
                formListeners.delete(callback);
            };
        },

        getFormSnapshot,
        getValues: getRenderedValues,
        isSubmitInProgress: () => submitValidating || isSubmitting,
        setFieldValue,
        handleBlur,
        submit,
        validateField,
        executeAction,
        updateData,
        reset,

        destroy() {
            for (const timer of asyncTimers.values()) {
                clearTimeout(timer);
            }
            asyncTimers.clear();
            fieldListeners.clear();
            formListeners.clear();
        },
    };
};

export interface FormStore {
    readonly config: { readonly id: string; readonly elements: readonly FormElementConfig[] };
    subscribeField(fieldId: string, callback: () => void): () => void;
    getFieldSnapshot(fieldId: string): FieldState;
    subscribeForm(callback: () => void): () => void;
    getFormSnapshot(): FormStatus;
    getValues(): FormValues;
    isSubmitInProgress(): boolean;
    setFieldValue(fieldId: string, value: FormFieldValue): void;
    handleBlur(fieldId: string): void;
    submit(callbacks?: SubmitCallbacks): Promise<SubmitResult>;
    validateField(fieldId: string): Promise<string | undefined>;
    executeAction(action: (context: ActionContext) => void | Promise<void>): void | Promise<void>;
    updateData(data?: FormDataMap): void;
    reset(): void;
    destroy(): void;
}
